import * as React from 'react';
import {Mutation} from 'react-apollo';

import {IntegrationWidget} from 'shells/integration-widget/integration-widget';
import O365LogoSvg from 'shells/logos/office-365.svg';

import type {
    setOffice365MailSyncIsEnabled as setOffice365MailSyncIsEnabledMutation,
    setOffice365MailSyncIsEnabledVariables as setOffice365MailSyncIsEnabledMutationVariables,
    UserEmail as UserEmailFragment,
} from 'nutshell-graphql-types';

import {authToService} from '../../integrations/auth-to-service';
import {ClearIntegration} from '../../integrations/clear-integration';
import {getAuthUrl} from '../../integrations/integration-mutation-response-helpers';

import {QUERIES_TO_REFETCH_AFTER_EMAIL_UPDATE} from '../constants';
import SET_OFFICE365_MAIL_SYNC_IS_ENABLED from '../graphql/mutations/set-office365-mail-sync-is-enabled.graphql';

import {updateCacheFromMutation} from './update-cache-from-mutation';

const o365Logo = <O365LogoSvg width={24} />;
const windowFeatures = {
    toolbar: 'no',
    location: 'no',
    width: 600,
    height: 500,
} as const;

class Office365EnableMutation extends Mutation<
    setOffice365MailSyncIsEnabledMutation,
    setOffice365MailSyncIsEnabledMutationVariables
> {}

type Props = {
    email: UserEmailFragment;
    onAuthStart: () => void;
    onAuthSuccess: (arg1?: React.ReactElement<any> | null | undefined) => Promise<any>;
    onAuthError: (arg1: string[]) => void;
    errors?: string[];
};

export function EmailWidgetO365(props: Props) {
    const [isAuthenticating, setIsAuthenticating] = React.useState<boolean>(false);

    return (
        <ClearIntegration>
            {({clearIntegration}) => (
                <Office365EnableMutation
                    mutation={SET_OFFICE365_MAIL_SYNC_IS_ENABLED}
                    update={updateCacheFromMutation}
                    // @ts-expect-error
                    refetchQueries={QUERIES_TO_REFETCH_AFTER_EMAIL_UPDATE}
                >
                    {(enableOffice365, {loading: isMutationInFlight}) => {
                        const hasErrors = props.errors && props.errors.length > 0;
                        let status =
                            props.email.isSyncEnabled && !hasErrors ? 'enabled' : 'disabled';
                        if (isMutationInFlight || isAuthenticating) {
                            status = props.email.isSyncEnabled ? 'disabling' : 'enabling';
                        }
                        const statusMessage = hasErrors
                            ? 'Needs attention'
                            : `Email sync is turned ${props.email.isSyncEnabled ? 'on' : 'off'}`;

                        return (
                            <IntegrationWidget
                                key={props.email.id}
                                account={props.email.address}
                                // @ts-expect-error
                                status={status}
                                statusMessage={statusMessage}
                                errors={props.errors}
                                logo={o365Logo}
                                onConnectClick={() => {
                                    enableOffice365({variables: {isEnabled: true}})
                                        .then(getAuthUrl)
                                        .then((authUrl) => {
                                            // If we get an authUrl back, then we need to auth
                                            if (authUrl) {
                                                props.onAuthStart();
                                                setIsAuthenticating(true);
                                                authToService({
                                                    authUrl,
                                                    windowFeatures,
                                                    authCheckUrl: '/office365/has-auth',
                                                })
                                                    .then(({hasAuth}) => {
                                                        // If we're authed and requesting more scopes, this will come
                                                        // back as true no matter what.  But handling it differently
                                                        // isn't easy and the user experience isn't awful, we'll just go
                                                        // back to the disabled state.
                                                        if (hasAuth) {
                                                            props.onAuthSuccess().then(() => {
                                                                setIsAuthenticating(false);
                                                            });
                                                        }
                                                    })
                                                    .catch((errMessages) => {
                                                        props.onAuthError(errMessages);
                                                        setIsAuthenticating(false);
                                                    });
                                            }
                                        });
                                }}
                                onDisconnectClick={() => {
                                    enableOffice365({variables: {isEnabled: false}}).catch(
                                        (error) => {
                                            props.onAuthError(error);
                                        }
                                    );
                                }}
                                actionStyle='switch'
                                revokeWarning='This will also remove this account from email sending and calendar sync.'
                                onRevokeClick={() => {
                                    props.onAuthStart();
                                    clearIntegration('OFFICE365')
                                        .then(() => {
                                            props.onAuthSuccess();
                                        })
                                        // TODO: does this actually work?
                                        .catch((errMsg) => {
                                            props.onAuthError(errMsg);
                                        });
                                }}
                            />
                        );
                    }}
                </Office365EnableMutation>
            )}
        </ClearIntegration>
    );
}
